/**
 * @fileoverview A sortable report table: the org Dashboard's Programs and
 * Instructors tabs, and a program viewer's Lessons and Students.
 *
 * Each column is `{ id, label, render(row), sortValue?(row), firstDir?, className? }`;
 * a column with a `sortValue` sorts from its header, the first column ascending
 * and the rest descending on first press (most first, for a number), unless
 * `firstDir` says otherwise: a column of names reads A to Z first. Cells after
 * the first are numbers by default (right-aligned, tabular); a text column
 * says so in `className`.
 * `renderAfter(row)` may return a full-width row to show under one, such as a
 * lesson's summary opened from its row.
 */

import { Fragment, useMemo, useState } from "react";
import { Icon } from "@/components/studio/primitives";
import { cn } from "@/lib/utils";

/**
 * A sortable report table. The first cell of each row holds the real link
 * (keyboard and screen readers reach the row's page through it); the row's
 * own click is a larger target for the pointer, as the program list's cards
 * are.
 */
export default function ReportTable({
  columns,
  rows,
  rowKey,
  onRowPress,
  canPress = () => true,
  defaultSort,
  footer,
  testId,
  empty,
  renderAfter,
}) {
  const [sort, setSort] = useState(defaultSort);
  const sorted = useMemo(() => {
    const col = columns.find((c) => c.id === sort.id);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue(a);
      const vb = col.sortValue(b);
      if (typeof va === "string" || typeof vb === "string") return String(va).localeCompare(String(vb)) * dir;
      return ((va ?? -Infinity) - (vb ?? -Infinity)) * dir;
    });
  }, [columns, rows, sort]);

  const toggle = (id) =>
    setSort((s) =>
      s.id === id
        ? { id, dir: s.dir === "desc" ? "asc" : "desc" }
        : { id, dir: columns.find((c) => c.id === id)?.firstDir || (id === columns[0].id ? "asc" : "desc") },
    );

  if (rows.length === 0) {
    return <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">{empty}</div>;
  }

  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--studio-divider)]">
            {columns.map((col, i) => {
              const active = sort.id === col.id;
              return (
                <th
                  key={col.id}
                  scope="col"
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                  className={cn(
                    "whitespace-nowrap py-3 font-sans text-[12px] font-semibold text-[var(--text-tertiary)]",
                    i === 0 ? "pl-5 pr-3 text-left" : "px-3 text-right",
                    i === columns.length - 1 && "pr-5",
                    col.className,
                  )}
                >
                  {col.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggle(col.id)}
                      data-testid={`${testId}-sort-${col.id}`}
                      className={cn(
                        "inline-flex items-center gap-1 transition-colors hover:text-[var(--text-primary)]",
                        active && "text-[var(--text-primary)]",
                      )}
                    >
                      {col.label}
                      <Icon
                        name={active && sort.dir === "asc" ? "chevronUp" : "chevronDown"}
                        size={12}
                        strokeWidth={2}
                        className={active ? "" : "opacity-0"}
                      />
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const after = renderAfter?.(row);
            return (
              <Fragment key={rowKey(row)}>
                <tr
                  data-testid={`${testId}-row-${rowKey(row)}`}
                  onClick={onRowPress && canPress(row) ? () => onRowPress(row) : undefined}
                  className={cn(
                    "border-b border-[var(--studio-divider)] last:border-b-0",
                    onRowPress && canPress(row) && "cursor-pointer transition-colors duration-150 hover:bg-[var(--studio-overlay)]",
                    after && "border-b-0",
                  )}
                >
                  {columns.map((col, i) => (
                    <td
                      key={col.id}
                      className={cn(
                        "py-3 align-middle font-sans text-[14px]",
                        i === 0 ? "pl-5 pr-3 text-left" : "px-3 text-right font-mono tabular-nums text-[var(--text-primary)]",
                        i === columns.length - 1 && "pr-5",
                        col.className,
                      )}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
                {after && (
                  <tr className="border-b border-[var(--studio-divider)] last:border-b-0" data-testid={`${testId}-after-${rowKey(row)}`}>
                    <td colSpan={columns.length} className="px-5 pb-4 pt-0">
                      {after}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
              {columns.map((col, i) => (
                <td
                  key={col.id}
                  className={cn(
                    "py-3 font-sans text-[13px] font-semibold",
                    i === 0 ? "pl-5 pr-3 text-left text-[var(--text-primary)]" : "px-3 text-right font-mono tabular-nums text-[var(--text-primary)]",
                    i === columns.length - 1 && "pr-5",
                    col.className,
                  )}
                >
                  {footer[col.id] ?? ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
